import { getRandomId } from "client/utils/getRandomId";
import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

interface Options {
    onPointerDown?: () => void;
    onPointerMove?: (left: number, top: number) => void;
    onPointerUp?: () => void;
    onEnter?: () => void;
    onLeave?: () => void;
    positionType?: "relative" | "fixed";
}

let draggingId: string | null = null;

export const useDrag = (options: Options = {}) => {
    const isPointerDownRef = useRef(false);
    const lastPointerDownRef = useRef<{ clientX: number; clientY: number } | null>(null);
    const id = useMemo(() => getRandomId(), []);
    const [isDragging, setIsDragging] = useState(false);

    function setPointerDown(clientX: number, clientY: number) {
        isPointerDownRef.current = true;
        lastPointerDownRef.current = { clientX, clientY };
        draggingId = id;
        setIsDragging(true);
    }

    function isActive() {
        return draggingId === null || draggingId === id;
    }

    function onPointerDown(e: ReactPointerEvent) {
        if (!isActive()) return;
        e.stopPropagation();
        setPointerDown(e.clientX, e.clientY);
        options.onPointerDown?.();
    }

    function handleMove(clientX: number, clientY: number) {
        if (!isPointerDownRef.current || !lastPointerDownRef.current) return;
        switch (options.positionType) {
            case undefined:
            case "relative":
                options.onPointerMove?.(
                    clientX - lastPointerDownRef.current.clientX,
                    clientY - lastPointerDownRef.current.clientY,
                );
                break;
            case "fixed":
                options.onPointerMove?.(clientX, clientY);
                break;
            default:
                const _: never = options.positionType;
                _;
                throw new Error("Invalid Position Type");
        }
        lastPointerDownRef.current = {
            clientX: clientX,
            clientY: clientY,
        };
    }

    function onPointerMove(e: ReactPointerEvent) {
        if (!isActive()) return;
        e.stopPropagation();
        handleMove(e.clientX, e.clientY);
    }

    function setPointerUp() {
        if (!isActive()) return;
        isPointerDownRef.current = false;
        lastPointerDownRef.current = null;
        draggingId = null;
        setIsDragging(false);
    }

    function onPointerUp(_e: ReactPointerEvent) {
        if (!isActive()) return;
        _e.stopPropagation();
        setPointerUp();
        options.onPointerUp?.();
    }

    function onPointerEnter() {
        if (!isActive()) return;
        options.onEnter?.();
    }

    function onPointerLeave() {
        if (!isActive()) return;
        if (isPointerDownRef.current) {
            setBodyEventListeners();
        } else {
            removeBodyEventListeners();
        }
        options.onLeave?.();
    }

    function onBodyPointerUp(_e: PointerEvent) {
        if (!isActive()) return;
        setPointerUp();
        removeBodyEventListeners();
        options.onPointerUp?.();
    }

    function windowPointerMove(e: PointerEvent) {
        if (!isActive()) return;
        e.preventDefault();
    }

    function onBodyPointerMove(e: PointerEvent) {
        if (!isActive()) return;
        handleMove(e.clientX, e.clientY);
    }

    const setBodyEventListeners = () => {
        document.body.addEventListener("pointermove", onBodyPointerMove);
        document.body.addEventListener("pointerup", onBodyPointerUp);
        window.addEventListener("pointermove", windowPointerMove);
    };

    const removeBodyEventListeners = () => {
        document.body.removeEventListener("pointermove", onBodyPointerMove);
        document.body.removeEventListener("pointerup", onBodyPointerUp);
        window.removeEventListener("pointermove", windowPointerMove);
    };

    return {
        dragHandlers: { onPointerDown, onPointerMove, onPointerUp, onPointerLeave, onPointerEnter },
        isDragging,
    };
};
